import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Map, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';

export default function AdminTours() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [denied, setDenied] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => api.adminTours().then(({ data }) => setData(data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); });
  useEffect(() => { document.title = 'Tours | Admin'; if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  useEffect(() => { if (user) load(); }, [user]);

  const reseed = async () => { setBusy(true); setMsg(''); try { const { data } = await api.adminToursReseed(); setMsg(`Reseeded ${data.tours} tours, ${data.offers} offers`); load(); } catch (e) { setMsg('Reseed failed (admin only)'); } finally { setBusy(false); } };

  if (loading) return <Layout><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></Layout>;
  if (denied) return <Layout><div className="max-w-2xl mx-auto py-24 text-center"><AlertTriangle className="h-10 w-10 mx-auto mb-3 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Staff access required</h1></div></Layout>;

  const s = data?.stats;
  const KPI = ({ label, value }) => (<Card className="p-4 rounded-2xl"><p className="text-xs text-muted-foreground">{label}</p><p className="text-2xl font-semibold tabular">{value}</p></Card>);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-5">
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Map className="h-6 w-6 text-accent" /> Tours</h1>
          <div className="flex items-center gap-2">
            {msg && <span className="text-xs text-muted-foreground">{msg}</span>}
            <Button onClick={reseed} disabled={busy} variant="outline" className="rounded-xl" data-testid="tours-reseed"><RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} />Reseed demo tours</Button>
          </div>
        </div>
        {!s ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
          <>
            <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-7 mb-6" data-testid="tours-kpis">
              <KPI label="Total tours" value={s.total_tours} />
              <KPI label="Live tours" value={s.live_tours} />
              <KPI label="Demo/mock" value={s.demo_tours} />
              <KPI label="Offers" value={s.offers} />
              <KPI label="Clicks" value={s.clicks} />
              <KPI label="Real clicks" value={s.real_clicks} />
              <KPI label="Conversions" value={s.conversions} />
            </div>
            <Card className="p-4 rounded-2xl">
              <h3 className="font-semibold mb-3">Tour catalog</h3>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2">Tour</th><th>Destination</th><th>Duration</th><th>Best price</th><th>Rating</th><th>Organic</th><th>Sponsored</th><th>Clicks</th></tr></thead>
                  <tbody data-testid="admin-tours-table">
                    {data.tours.map((t) => (
                      <tr key={t.tour_id} className="border-b border-border last:border-0">
                        <td className="py-2 pr-2"><span className="font-medium">{t.title}</span> {t.is_demo && <Badge variant="outline" className="text-[10px] text-amber-600 ml-1">demo</Badge>}</td>
                        <td className="capitalize text-xs">{t.destination_slug} · {t.country}</td>
                        <td className="text-xs">{t.duration_days}D/{t.duration_nights}N</td>
                        <td className="tabular">₹{(t.best_price || 0).toLocaleString('en-IN')}</td>
                        <td>{t.rating}</td>
                        <td className="tabular font-medium">{t.organic_score}</td>
                        <td>{t.featured ? <Badge className="text-[10px] bg-accent text-accent-foreground">featured</Badge> : ''}</td>
                        <td>{t.clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4">
                <p className="text-xs text-muted-foreground mb-2">Top tour destinations</p>
                <div className="flex flex-wrap gap-2">{(s.top_destinations || []).map((d) => <Badge key={d.slug} variant="outline" className="rounded-full capitalize">{d.slug} · {d.tour_count}</Badge>)}</div>
              </div>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
